<template>
  <b-container
    :fluid="true"
    tag="section"
    class="dashboard">
    <b-row class="title ml-0 mr-0 mb-4">
      <b-col
        md="12"
        class="left-40">
        <h2 class="sb2_reports_title">
          <span class="flex" >
            <router-link
              :to="{ name: 'OrgAnalytics', params: { organisationId: currentOrganisation.id } }"
              class="back-btn flex flex-align-center show-hand"
              tag="span" >
              <SBIcon
                class="mr-2 rotate-90"
                :icon="SBIconList.DOWN_ARROW"
                :color="SBColors.greyPrimary"
                :size="12" />
              {{ (!isIntegration ? $route.params.section : `${targetIntegration.name} conversion rates`) | firstUppercase }} report
            </router-link>
          </span>
        </h2>
      </b-col>
    </b-row>
    <b-row class="sticky-top">
      <b-col
        md="12"
        class="action-bar pl-0 pr-0">
        <!-- Actions bar -->
        <!-- Search section -->
        <div
          class="flex space-between settings--box"
          :class="{'no-border': $route.params.section && $route.params.section === 'members'}" >
          <div class="flex">
            <!-- Time FILTER -->
            <DatePicker
              ref="dateRange"
              :start-date="null"
              :omit-shortcuts="[]"
              :week-mode="isWeekMode"
              :month-mode="isMonthMode"
              @datesChanged="dateRangeChange"/>

            <!-- Range FILTER -->
            <multiselect
              v-model="rangeFilter"
              :disabled="false"
              placeholder="All numbers"
              class="flex-options hide-tags no-selected-background range-filter up-down left-10 right-align sb2-bordered sb2-hoverable sb2-focusable"
              :class="{ hide: $route.params.section && $route.params.section === 'members' }"
              :options="rangeFilterOptions"
              label="name"
              track-by="alias"
              :multiple="false"
              :show-labels="false"
              :show-no-results="false"
              :close-on-select="true"
              :clear-on-select="false"
              :preserve-search="true"
              :allow-empty="false"
              :searchable="false"
              :preselect-first="true" >
              <template
                slot="option"
                slot-scope="props">
                <SBIcon
                  :icon="SBIconList.DONE_TICK"
                  :color="SBColors.bluePrimary"
                  :size="9"/>
                <span class="ellipsis">
                  {{ props.option.name }}
                </span>
              </template>
            </multiselect>

            <b-button
              class="download flex center-all flex-options hide-tags no-selected-background range-filter left-10 sb2-bordered sb2-hoverable sb2-focusable"
              @click="downloadReport">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.DOWNLOAD_SQUARE"
                :color="SBColors.blackPrimary"
                :size="12"/>
              <span>Download report</span>
            </b-button>
          </div>

          <!-- Number FILTER -->
          <FilterSingle
            v-model="numbersFilter"
            :items="numbersToFilter"
            track-by="id"
            label="name"
            sub-label="phone"
            track-icon="icon"
            group-title="Numbers"
            placement="right"
            :show-prefix-icon="false"
            :show-prefix-tick="false" />
        </div>
      </b-col>
    </b-row>
    <b-row
      v-if="!$route.params.section || $route.params.section !== 'members'"
      class="report-header">
      <b-col class="col-4 md-12 p-0 dashed-right h-100">
        <span class="date-range">{{ startDate | tableDate }} - {{ endDate | tableDate }}</span>
        <div
          v-if="loadingData"
          class="member-loader flex column center-all" >
          <div class="member-loader-wrapper flex center-all">
            <ChartLoader
              :dark-mode="true"
              width="28" />
          </div>
          <p>Loading data</p>
        </div>
        <ScrollArea
          v-else
          ref="conversationScrollbar"
          class="details-container scrollbar-light">
          <div
            v-for="(detail, index) in Object.keys(detailsData)"
            :key="`d-${index}`"
            class="details"
            :class="{ 'ml-3': detail.toString().startsWith('BIC') || detail.toString().startsWith('UIC') }">
            <span class="ellipsis mr-3">{{ detail }}</span>
            <span class="no-wrap">{{ detailsData[detail] === 'No activity' ? '-' : detailsData[detail] }}</span>
          </div>
        </ScrollArea>
      </b-col>
      <b-col class="col-8 md-12 p-0">
        <SbApexChart
          :title="chartTitleComputed"
          report-title="Messages"
          height="500"
          :force-loader="loadingData"
          :no-data-if-zero="false"
          :start-date="startDate"
          :end-date="endDate"
          :chart-series="chartsData.messages.chartSeries"
          :data-series="chartsData.messages.dataSeries"
          :details-elements="chartsData.messages.chartDetails"
          :no-activity-zero="isIntegration"
          :hide-details="true"
          :resolution="rangeFilter ? rangeFilter.alias : null"
          :hide-report-btn="true"
          :dark-mode="true"
          :hourly="rangeFilter && rangeFilter.alias === 'hour'"
          :colors="chartColors"
          :hover-right-corner-detail="timezoneDetails"
          :time-values="$route.params.section === 'average reply time'"
          :percentage="isIntegration"
          :value-suffix="isIntegration ? '%' : ''"
          @hover-index="hoverIndexChange" />
      </b-col>
    </b-row>
    <b-row>
      <!-- Data grid -->
      <div
        ref="tableGrid"
        :style="gridVars"
        class="table__grid clickable">

        <span
          v-for="(column, index) in tableColumnsComputed"
          :key="`th-${index}`"
          class="column-name sticky-top sticky-second" >
          <template v-if="rangeFilter && rangeFilter.alias === 'hour' && column === 'Date' && (isIntegration || ($route.params.section && $route.params.section !== 'members'))">
            Time
          </template>
          <template v-else>
            {{ column }}
          </template>
        </span>

        <!-- TODO add hover color but remove hand - no click event is needed -->
        <!-- :class="[`hover-${index}`, 'flex', 'flex-align-center', {hide: !filteredNumbersIds.includes(business.id)}]" -->
        <!-- @mouseleave="removeHover(index)"
          @mouseenter="addHover(index)" -->

        <template v-if="tableData && Object.keys(tableData).length">
          <template v-for="(point, pointIndex) in tablePoints" >
            <div
              v-for="(column, index) in tableColumnsComputed"
              :key="`tgi${pointIndex}-${index}`"
              class="grid-item"
              :class="[{ hide: loadingData }, `hover-${pointIndex}`, 'show-arrow']"
              @mouseleave="removeHover(pointIndex)"
              @mouseenter="addHover(pointIndex)"
              @click="selectRow(pointIndex)" >
              <div
                class="flex flex-align-center">
                <SBAvatar
                  v-if="$route.params.section === 'members' && column === 'Member'"
                  class="no-shrink avatar mr-2"
                  size="xs-button"
                  :show-name="false"
                  :show-data="true"
                  :character-count="1"
                  :person="tableData[point].Member"
                  :show-client="true" />
                <p
                  v-if="$route.params.section === 'members' && column === 'Member'"
                  class="ellipsis"
                  :inner-html.prop="tableData && tableData[point] ? tableData[point].Member.name : ''" />
                <p
                  v-else
                  class="ellipsis"
                  :class="{muted: tableData && tableData[point] && tableData[point][column] && typeof tableData[point][column] === 'string' && tableData[point][column].indexOf('No activity') >= 0}"
                  :inner-html.prop="tableData && tableData[point] ? tableData[point][column] : 0" />
              </div>
            </div>
          </template>
        </template>

      </div>
      <Spinner
        v-if="loadingData"
        style="height: 38px;"
        class="my-4 full-width"
        :size="20"
        :thickness="2" />
      <div
        v-if="!loadingData && !datePoints.length && $route.params.section !== 'members'"
        class="no-border no-data flex center-all greedy">
        <span class="wrapper flex center-all">
          <span class="emoji">😐</span>
          <p>No data is available</p>
        </span>
      </div>
    </b-row>
  </b-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DatePicker from '@/components/common/DatePicker';
import Multiselect from 'vue-multiselect';
import { duration } from 'moment';

import SbApexChart from '@/components/business/analytics/charts/SbApexChart';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import FilterSingle from '@/components/common/FilterSingle';
import ScrollArea from '../../common/ScrollArea';
import api from '@/helpers/api';
import Spinner from '../../common/Spinner';
import SBAvatar from '../../common/Avatar';
import ToasterMixin from '@/mixins/Toaster';
import ChartLoader from '@/components/animatedIcons/ChartLoader';
import { isAmPm } from '@/helpers/dateHelpers';

export default {
  name: 'Report',
  components: {
    SbApexChart,
    Multiselect,
    FilterSingle,
    DatePicker,
    SBIcon,
    Spinner,
    ScrollArea,
    SBAvatar,
    ChartLoader,
  },
  mixins: [
    ToasterMixin,
  ],
  data() {
    return {
      preventDataFetch: false,
      rangeFilter: null,
      hideModes: [],
      numbersFilter: [],
      startDate: null,
      initialized: false,
      endDate: null,
      detailsData: {},
      memberCountSorted: [],
      loadingData: true,
      hoverIndex: null,
      chartsData: {
        messages: {
          chartSeries: null,
          chartDetails: [
            { name: 'in', text: 'sent' },
            {
              name: 'out',
              text: 'received',
            },
            {
              name: 'bot',
              text: 'bot',
            },
          ],
          dataSeries: null,
        },
      },
      datePoints: [],
      analyticsData: null,
      chartColors: {
        line: '#7eb4f5',
        crosshairs: SBColors.grayPrimary,
        marker: '#7eb4f5',
        axisBorder: '#506674',
      },
      tableData: null,
      tableColumns: {
        integration: {
          columns: {
            'Conversion rate': ['average_reply_time.seconds', 'average_reply_time.seconds'],
            Contacted: ['average_reply_time.seconds'],
            Replied: ['average_reply_time.seconds'],
            Date: ['datePoint'],
          },
          configuration: {
            'grid-template-columns': 'minmax(50px, auto) minmax(50px, auto)  minmax(0, auto) minmax(0, min-content)',
          },
        },
        // Members are generated differently and the columns and configuration here are just to define the table.
        members: {
          columns: {
            Member: [],
            'Total conversations': [],
            'Outbound messages': [],
            'Average reply time': [],
            Date: [],
          },
          configuration: {
            'grid-template-columns': 'minmax(50px, auto) minmax(50px, auto) minmax(0, auto) minmax(50px, auto) minmax(0, min-content)',
          },
        },
        'average reply time': {
          columns: {
            'Average reply time': ['average_reply_time.seconds', 'average_reply_time.seconds'],
            '0 - 1 hours': ['average_reply_time.seconds'],
            '1 - 8 hours': ['average_reply_time.seconds'],
            '8 - 24 hours': ['average_reply_time.seconds'],
            '> 24 hours': ['average_reply_time.seconds'],
            Date: ['datePoint'],
          },
          configuration: {
            'grid-template-columns': 'minmax(50px, auto) minmax(50px, auto)  minmax(0, auto) minmax(0, auto) minmax(0, auto) minmax(0, min-content)',
          },
        },
        messages: {
          columns: {
            'Total messages': ['messages.inbound', 'messages.outbound'],
            Inbound: ['messages.inbound'],
            Outbound: ['messages.outbound'],
            'WA Message Templates': ['messages.template'],
            'Automated responses': ['messages.bot'],
            // 'Web': '',
            // 'Mobile': '',
            // 'API': '',
            // 'Front': '',
            Date: ['datePoint'],
          },
          configuration: {
            'grid-template-columns': 'minmax(50px, auto) minmax(50px, auto)  minmax(0, auto) minmax(0, auto) minmax(0, auto) minmax(0, min-content)',
          },
        },
        conversations: {
          columns: {
            'Total conversations': ['conversations.channels.whatsapp.UIC.UIC (legacy)', 'conversations.channels.whatsapp.BIC.BIC (legacy)', 'conversations.channels.whatsapp.BIC.BIC Authentication', 'conversations.channels.whatsapp.BIC.BIC Marketing', 'conversations.channels.whatsapp.BIC.BIC Utility', 'conversations.channels.whatsapp.UIC.UIC Service', 'conversations.channels.whatsapp.UIC.UIC Free Entry Point'],
            New: ['conversations.channels.new'],
            'BIC (legacy)': ['conversations.channels.whatsapp.BIC.BIC (legacy)'],
            // 'WA FEP': ['conversations.fep_conversations'],
            // 'Mobile': '',
            // 'API': '',
            // 'Front': '',
            'BIC Authentication': ['conversations.channels.whatsapp.BIC.BIC Authentication'],
            'BIC Marketing': ['conversations.channels.whatsapp.BIC.BIC Marketing'],
            'BIC Utility': ['conversations.channels.whatsapp.BIC.BIC Utility'],
            'UIC (legacy)': ['conversations.channels.whatsapp.UIC.UIC (legacy)'],
            'UIC Service': ['conversations.channels.whatsapp.UIC.UIC Service'],
            'UIC Free Entry Point': ['conversations.channels.whatsapp.UIC.UIC Free Entry Point'],
            Date: ['datePoint'],
          },
          configuration: {
            'grid-template-columns': 'minmax(50px, auto) minmax(50px, auto)  minmax(0, auto) minmax(0, auto) minmax(0, auto) minmax(0, auto) minmax(0, auto) minmax(0, auto) minmax(0, auto) minmax(0, min-content)',
          },
        },
        contacts: {
          columns: {
            'Total new': [''],
            Date: ['datePoint'],
          },
          configuration: {
            'grid-template-columns': 'minmax(50px, auto) minmax(0, min-content)',
          },
        },
      },
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
      currentOrganisationMembers: 'organisation/currentOrganisationMembers',
      currentOrganisationMembersLookup: 'organisation/currentOrganisationMembersLookup',
      currentOrganisationBusinesses: 'organisation/currentOrganisationBusinesses',
      // organisationTeams: 'organisation/organisationTeams',
      datePickerResolution: 'helpers/datePickerResolution',
      datePickerNumbersFilter: 'helpers/datePickerNumbersFilter',
      currentOrganisationIntegrations: 'organisation/currentOrganisationIntegrations',
      // userRegion: 'user/region',
      numberIntegrationMap: 'organisation/numberIntegrationMap',
    }),
    tablePoints() {
      return this.$route.params.section === 'members' ? this.memberCountSorted : this.datePoints;
    },
    targetIntegration() {
      return this.isIntegration && this.currentOrganisationIntegrations.length
        ? this.currentOrganisationIntegrations.find(integration => integration.code === this.$route.params.integration)
        : {
          name: '',
        };
    },
    chartTitleComputed() {
      let title;
      if (this.isIntegration) {
        title = 'CONVERSION RATE';
      } else {
        const infix = this.$route.params.section !== 'contacts' ? '' : 'new ';
        switch (this.$route.params.section) {
          case 'average reply time':
            title = 'AVERAGE REPLY TIME';
            break;
          default:
            title = `Total ${infix}${this.$route.params.section}`;
            break;
        }
      }

      return title;
    },
    tableColumnsComputed() {
      let tableColumnsComputed;
      if (!this.isIntegration) {
        tableColumnsComputed = Object.keys(this.tableColumns[this.$route.params.section].columns);
      } else {
        tableColumnsComputed = Object.keys(this.tableColumns.integration.columns);
      }

      return tableColumnsComputed;
    },
    gridVars() {
      return {
        '--grid': '40px',
        '--grid-template-columns': this.isIntegration ? this.tableColumns.integration.configuration['grid-template-columns'] : this.tableColumns[this.$route.params.section].configuration['grid-template-columns'],
      };
    },
    isMonthMode() {
      return this.rangeFilter && this.rangeFilter.alias === 'month';
    },
    isWeekMode() {
      return this.rangeFilter && this.rangeFilter.alias === 'week';
    },
    isIntegration() {
      return !!this.$route.params.integration;
    },
    integrationName() {
      return !this.isIntegration ? '' : this.$route.params.integration;
    },
    rangeFilterOptions() {
      const rangeFilterOptions = [
        {
          alias: 'hour',
          name: 'Hourly',
        },
        {
          alias: 'day',
          name: 'Daily',
        },
        {
          alias: 'week',
          name: 'Weekly',
        },
        {
          alias: 'month',
          name: 'Monthly',
        },
      ];
      return rangeFilterOptions.filter(option => !this.hideModes.includes(option.alias));
    },
    membersInFocus() {
      let userIds = [];
      if (this.numbersFilter.length) {
        this.currentOrganisationMembers.forEach((member) => {
          if (this.numbersFilter.some(filteredNumber => member.businesses.includes(filteredNumber))) {
            userIds.push(member.id);
          }
        });
      } else {
        userIds = this.currentOrganisationMembers.map(member => member.id);
      }

      return userIds;
    },
    numbersToFilter() {
      const businessesWithEmoji = this.currentOrganisationBusinesses.map((number, index) => {
        const item = {
          id: number.id,
          name: number.name,
          phone: number.phone,
          icon: number.emoji,
        };
        if (this.numberIntegrationMap[number.id] && this.numberIntegrationMap[number.id].length === 1) {
          delete item.icon;
          item.integration = this.numberIntegrationMap[number.id][0];
        } else if (this.numberIntegrationMap[number.id] && this.numberIntegrationMap[number.id].length) {
          delete item.icon;
          item.integrationCount = this.numberIntegrationMap[number.id].length;
        }
        return item;
      });
      return businessesWithEmoji;
    },
    total() {
      let total = [];
      if (this.isIntegration) {
        total = 0;
      } else {
        switch (this.$route.params.section) {
          case 'messages':
            total = ['messages.inbound', 'messages.outbound'];
            break;
          case 'conversations':
            total = ['conversations.channels.whatsapp.UIC.UIC (legacy)', 'conversations.channels.whatsapp.BIC.BIC (legacy)', 'conversations.channels.whatsapp.BIC.BIC Authentication', 'conversations.channels.whatsapp.BIC.BIC Marketing', 'conversations.channels.whatsapp.BIC.BIC Utility', 'conversations.channels.whatsapp.UIC.UIC Service', 'conversations.channels.whatsapp.UIC.UIC Free Entry Point'];
            break;
          case 'contacts':
            total = ['conversations.channels.whatsapp.UIC.UIC (legacy)', 'conversations.channels.whatsapp.BIC.BIC (legacy)'];
            break;
          default:
            break;
        }
      }

      return total;
    },
    details() {
      let details = {};
      if (this.isIntegration) {
        details = {
          'CONVERSION RATE': [''],
          CONTACTED: ['count'],
          REPLIED: ['answered_count'],
        };
      } else {
        switch (this.$route.params.section) {
          case 'average reply time':
            details = {
              'AVERAGE REPLY TIME': ['integration.name.datum.count.seconds', 'average_reply_time.seconds'],
              '0 - 1 HOURS': ['average_reply_time.seconds'],
              '1 - 8 HOURS': ['average_reply_time.seconds'],
              '8 - 24 HOURS': ['average_reply_time.seconds'],
              '> 24 HOURS': ['average_reply_time.seconds'],
            };
            break;
          case 'messages':
            details = {
              'TOTAL MESSAGES': ['messages.inbound', 'messages.outbound'],
              INBOUND: ['messages.inbound'],
              OUTBOUND: ['messages.outbound'],
              'WA MESSAGE TEMPLATES': ['messages.template'],
              'AUTOMATED RESPONSES': ['messages.bot'],
            };
            break;
          case 'conversations':
            details = {
              'TOTAL CONVERSATIONS': ['conversations.channels.whatsapp.UIC.UIC (legacy)', 'conversations.channels.whatsapp.BIC.BIC (legacy)', 'conversations.channels.whatsapp.BIC.BIC Authentication', 'conversations.channels.whatsapp.BIC.BIC Marketing', 'conversations.channels.whatsapp.BIC.BIC Utility', 'conversations.channels.whatsapp.UIC.UIC Service', 'conversations.channels.whatsapp.UIC.UIC Free Entry Point'],
              NEW: ['conversations.channels.new'],
              'TOTAL BIC': ['conversations.channels.whatsapp.BIC.BIC (legacy)', 'conversations.channels.whatsapp.BIC.BIC Authentication', 'conversations.channels.whatsapp.BIC.BIC Marketing', 'conversations.channels.whatsapp.BIC.BIC Utility'],
              // 'WA FEP': ['conversations.fep_conversations'],
              'BIC (LEGACY)': ['conversations.channels.whatsapp.BIC.BIC (legacy)'],
              'BIC AUTHENTICATION': ['conversations.channels.whatsapp.BIC.BIC Authentication'],
              'BIC MARKETING': ['conversations.channels.whatsapp.BIC.BIC Marketing'],
              'BIC UTILITY': ['conversations.channels.whatsapp.BIC.BIC Utility'],
              'TOTAL UIC': ['conversations.channels.whatsapp.UIC.UIC (legacy)', 'conversations.channels.whatsapp.UIC.UIC Service', 'conversations.channels.whatsapp.UIC.UIC Free Entry Point'],
              'UIC (LEGACY)': ['conversations.channels.whatsapp.UIC.UIC (legacy)'],
              'UIC SERVICE': ['conversations.channels.whatsapp.UIC.UIC Service'],
              'UIC FREE ENTRY POINT': ['conversations.channels.whatsapp.UIC.UIC Free Entry Point'],
            };
            break;
          case 'contacts':
            details = {
              'TOTAL NEW': [''],
            };
            break;
          default:
            break;
        }
      }
      return details;
    },
    category() {
      let category = null;
      if (this.isIntegration) {
        category = 'integrations';
      } else {
        switch (this.$route.params.section) {
          case 'messages':
            category = 'messages';
            break;
          case 'conversations':
            category = 'conversations';
            break;
          case 'average reply time':
            category = 'average_reply_time';
            break;
          default:
            break;
        }
      }

      return category;
    },
    timezoneDetails() {
      const timezone = this.currentOrganisation.timezone.split('/');
      return timezone[1] ? `${timezone[1]} time` : timezone.length ? `${timezone[0]} time` : 'UTC time';
    },
  },
  watch: {
    analyticsData() {
      if (this.isIntegration) {
        this.processIntegrationsData();
        return;
      }

      if (this.$route.params.section === 'average reply time') {
        this.processAverageFirstReplyAnalyticsData();
        return;
      }

      if (this.$route.params.section && this.$route.params.section === 'members') {
        this.populateMembersTable();
        return;
      }

      const tableData = {};

      this.datePoints.forEach((datePoint) => {
        tableData[datePoint] = {};
        let numbers = Object.keys(this.analyticsData);
        numbers = numbers.filter(number => number !== 'date_points');

        numbers.forEach((number) => {
          const columnKeys = Object.keys(this.tableColumns[this.$route.params.section].columns);
          columnKeys.forEach((columnKey) => {
            const paths = this.tableColumns[this.$route.params.section].columns[columnKey];
            if (!tableData[datePoint][columnKey]) {
              tableData[datePoint][columnKey] = 0;
            }
            paths.forEach((path) => {
              if (path === 'datePoint') {
                if (this.rangeFilter.alias === 'hour') {
                  const isAmPmTest = isAmPm(this.$store.getters['user/region']);
                  tableData[datePoint][columnKey] = this.$moment(this.convertToUnixDate(datePoint) * 1000).format(isAmPmTest ? 'hh:mma - ' : 'HH:mm - ');
                  const dateValue = this.$moment(this.convertToUnixDate(datePoint) * 1000).format(', MMM DD, YYYY');
                  const endTime = this.$moment(this.convertToUnixDate(datePoint) * 1000).add(1, 'hour').format(isAmPmTest ? 'hh:mma' : ' kk:mm');
                  tableData[datePoint][columnKey] += endTime.trim() === '24:00' ? '23:59' : endTime;
                  if (this.datePoints.length > 24) {
                    tableData[datePoint][columnKey] += dateValue;
                  }
                } else if (this.rangeFilter.alias === 'week') {
                  const startDayFormated = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM DD, YYYY');
                  const endDayFormatted = this.$moment(this.convertToUnixDate(datePoint) * 1000).add(6, 'days').format('MMM DD, YYYY');
                  tableData[datePoint][columnKey] = `${startDayFormated} - ${endDayFormatted}`;
                } else if (this.rangeFilter.alias === 'month') {
                  const startDayFormated = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM, YYYY');
                  tableData[datePoint][columnKey] = `${startDayFormated}`; // - ${endDayFormatted}`;
                } else {
                  tableData[datePoint][columnKey] = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM DD, YYYY');
                }
              } else {
                tableData[datePoint][columnKey] += Number(this.$lodash.get(this.analyticsData[number], path) && this.$lodash.get(this.analyticsData[number], path)[datePoint] >= 0 ? this.$lodash.get(this.analyticsData[number], path)[datePoint] : 0);
              }
            });
          });
        });

        if (this.$route.params.section === 'contacts') {
          tableData[datePoint]['Total new'] += this.analyticsData[datePoint] ? this.analyticsData[datePoint] : 0;
        }
      });

      this.tableData = tableData;
    },
    rangeFilter(newValue, oldValue) {
      this.setDatePickerResolution(this.rangeFilter.alias);
      // Change on the range changes setup for the datePicker which then recalculates dates and emits change which triggers activity request.
      if (oldValue && this.initialized && (this.isIntegration || (this.$route.params.section && this.$route.params.section !== 'members'))) {
        this.showToaster(`<span class="emoji-fix">📅</span> Calendar was updated to ${this.rangeFilter.alias} range.`);
      }

      this.$nextTick(() => {
        this.getActivityData();
      });
    },
    numbersFilter() {
      this.satNumbersFilter(this.numbersFilter);
      this.getActivityData();
    },
    hideModes(newValue, oldValue) {
      if (this.hideModes.includes(this.rangeFilter.alias)) {
        this.rangeFilter = this.rangeFilterOptions[0];
      }

      if (newValue.length > oldValue.length && this.initialized && (this.isIntegration || (this.$route.params.section && this.$route.params.section !== 'members'))) {
        this.showToaster(`<span class="emoji-fix">↕️</span> View was updated to ${this.rangeFilter.name}.`);
      }
    },
    hoverIndex() {
      if (this.isIntegration) {
        this.processIntegrationsDetailsData();
      } else if (this.$route.params.section === 'contacts') {
        this.processContactsDetailsData();
      } else if (this.$route.params.section === 'average reply time') {
        this.processAverageFirstReplyDetailsData();
      } else if (this.isIntegration) {
        this.processIntegrationsDetailsData();
      } else {
        this.processDetailsData();
      }
    },
  },
  beforeMount() {
    if (this.datePickerNumbersFilter.length) {
      this.numbersFilter = this.datePickerNumbersFilter;
    }
  },
  mounted() {
    if (this.datePickerResolution) {
      this.rangeFilter = this.rangeFilterOptions.find(option => option.alias === this.datePickerResolution);
    }

    this.finalizeSplashView();
    this.getActivityData();

    setTimeout(() => {
      this.initialized = true;
    }, 0);
  },
  methods: {
    ...mapActions({
      finalizeSplashView: 'organisation/finalizeSplashView',
      setDatePickerResolution: 'helpers/setDatePickerResolution',
      satNumbersFilter: 'helpers/satNumbersFilter',
    }),
    removeHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        cell.classList.remove('hover-grid');
        cell.classList.remove('last');
      }
    },
    addHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        if (index === 0) {
          cell.classList.add('first');
        }
        cell.classList.add('hover-grid');
        if (index === cells.length - 1) {
          cell.classList.add('last');
        }
      }
    },
    selectRow() {
      // Do nothing for now
      // For members this will expand the row and show detailed graph for the member
    },
    populateMembersTable() {
      // this.datePoints
      let numbers = Object.keys(this.analyticsData);
      numbers = numbers.filter(number => number !== 'date_points');
      const members = this.membersInFocus;

      // Members table data START
      const memberCount = {};
      members.forEach((memberId) => {
        memberCount[memberId] = {
          Member: this.currentOrganisationMembersLookup[memberId],
          'Outbound messages': 0,
          count: 0,
          seconds: 0,
          'Average reply time': 0,
          'Total conversations': 0,
        };

        if (this.rangeFilter.alias === 'week') {
          const startDayFormated = this.$moment(this.startDate * 1000).format('MMM DD, YYYY');
          const endDayFormatted = this.$moment(this.endDate * 1000).format('MMM DD, YYYY');
          memberCount[memberId].Date = `${startDayFormated} - ${endDayFormatted}`;
        } else if (this.rangeFilter.alias === 'month') {
          const startDayFormated = this.$moment(this.startDate * 1000).format('MMM, YYYY');
          const endDayFormatted = this.$moment(this.endDate * 1000).format('MMM, YYYY');
          memberCount[memberId].Date = `${startDayFormated} - ${endDayFormatted}`; // - ${endDayFormatted}`;
        } else if (this.rangeFilter.alias === 'hour' && this.datePoints.length <= 24) {
          memberCount[memberId].Date = this.$moment(this.startDate * 1000).format('MMM DD, YYYY');
        } else {
          memberCount[memberId].Date = `${this.$moment(this.startDate * 1000).format('MMM DD, YYYY')} - ${this.$moment(this.endDate * 1000).format('MMM DD, YYYY')}`;
        }

        numbers.forEach((number) => {
          memberCount[memberId]['Total conversations'] += this.analyticsData[number] && this.analyticsData[number].conversations && this.analyticsData[number].conversations.members && this.analyticsData[number].conversations.members[memberId] ? Object.values(this.analyticsData[number].conversations.members[memberId]).reduce((prev, curr) => prev + curr, 0) : 0;
          memberCount[memberId]['Outbound messages'] += this.analyticsData[number].messages.members_outbound && this.analyticsData[number].messages.members_outbound[memberId] ? Object.values(this.analyticsData[number].messages.members_outbound[memberId]).reduce((prev, curr) => prev + curr, 0) : 0;
          memberCount[memberId].count += this.analyticsData[number].average_reply_time.members_seconds && this.analyticsData[number].average_reply_time.members_seconds[memberId] ? Object.values(this.analyticsData[number].average_reply_time.members_seconds[memberId]).reduce((prev, curr) => prev + curr.count, 0) : 0;
          memberCount[memberId].seconds += this.analyticsData[number].average_reply_time.members_seconds && this.analyticsData[number].average_reply_time.members_seconds[memberId] ? Object.values(this.analyticsData[number].average_reply_time.members_seconds[memberId]).reduce((prev, curr) => prev + curr.seconds, 0) : 0;
          const averageSeconds = memberCount[memberId].count ? Math.round(memberCount[memberId].seconds / memberCount[memberId].count) : 0;
          memberCount[memberId]['Average reply time'] = this.durationToString(averageSeconds) || 'No activity';
        });
      });

      const memberCountForSort = [];
      Object.keys(memberCount || {}).forEach((memberId) => {
        memberCountForSort.push({
          id: memberId,
          value: memberCount[memberId]['Total conversations'],
        });
      });

      this.memberCountSorted = memberCountForSort.sort((a, b) => b.value - a.value).map(item => item.id);

      this.tableData = { ...memberCount };
    },
    processIntegrationsData() {
      const tableData = {};

      const integration = this.$route.params.integration;

      this.datePoints.forEach((datePoint) => {
        tableData[datePoint] = {};
        let numbers = Object.keys(this.analyticsData);
        numbers = numbers.filter(number => number !== 'date_points');
        numbers.forEach((number) => {
          const numberData = this.analyticsData[number];
          if (!tableData[datePoint]['Conversion rate']) {
            tableData[datePoint]['Conversion rate'] = 0;
          }
          if (!tableData[datePoint].Contacted) {
            tableData[datePoint].Contacted = 0;
          }
          if (!tableData[datePoint].Replied) {
            tableData[datePoint].Replied = 0;
          }
          if (!tableData[datePoint].Date) {
            tableData[datePoint].Date = 0;
          }
          if (this.rangeFilter.alias === 'hour') {
            const isAmPmTest = isAmPm(this.$store.getters['user/region']);
            tableData[datePoint].Date = this.$moment(this.convertToUnixDate(datePoint) * 1000).format(isAmPmTest ? 'hh:mma - ' : 'HH:mm - ');
            const dateValue = this.$moment(this.convertToUnixDate(datePoint) * 1000).format(', MMM DD, YYYY');
            const endTime = this.$moment(this.convertToUnixDate(datePoint) * 1000).add(1, 'hour').format(isAmPmTest ? 'hh:mma' : ' kk:mm');
            tableData[datePoint].Date += endTime.trim() === '24:00' ? '23:59' : endTime;
            if (this.datePoints.length > 24) {
              tableData[datePoint].Date += dateValue;
            }
          } else if (this.rangeFilter.alias === 'week') {
            const startDayFormated = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM DD, YYYY');
            const endDayFormatted = this.$moment(this.convertToUnixDate(datePoint) * 1000).add(6, 'days').format('MMM DD, YYYY');
            tableData[datePoint].Date = `${startDayFormated} - ${endDayFormatted}`;
          } else if (this.rangeFilter.alias === 'month') {
            const startDayFormated = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM, YYYY');
            // const endDayFormatted = this.$moment(this.convertToUnixDate(datePoint) * 1000).add(1, 'months').format('MMM,YYYY');
            tableData[datePoint].Date = `${startDayFormated}`; // - ${endDayFormatted}`;
          } else {
            tableData[datePoint].Date = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM DD, YYYY');
          }
          tableData[datePoint].Contacted += numberData.integrations[integration] && Number(numberData.integrations[integration][datePoint].count) ? Number(numberData.integrations[integration][datePoint].count) : 0;
          tableData[datePoint].Replied += numberData.integrations[integration] && Number(numberData.integrations[integration][datePoint].answered_count) ? Number(numberData.integrations[integration][datePoint].answered_count) : 0;
        });
        tableData[datePoint]['Conversion rate'] = `${tableData[datePoint].Contacted ? Math.round(Number(tableData[datePoint].Replied / tableData[datePoint].Contacted * 100)) : 0}%`;

        if (tableData[datePoint]['Conversion rate'] === '0%') {
          tableData[datePoint]['Conversion rate'] = 'No activity';
        }
      });

      this.tableData = tableData;
    },
    convertToUnixDate(insightsDate) {
      let unixDate;
      const dateArray = insightsDate.split('-');
      switch (this.rangeFilter.alias) {
        case 'month':
          unixDate = this.$moment().year(dateArray[0]).month(dateArray[1] - 1).unix();
          break;
        case 'week':
          // console.log('w', dateArray[0], dateArray[1], this.$moment().year(dateArray[0]).isoWeek(dateArray[1]).day(0));
          unixDate = this.$moment().year(dateArray[0]).isoWeek(dateArray[1]).weekday(0).unix();
          // .tz("America/Toronto")
          break;
        case 'day':
          dateArray[1] -= 1; // month must be zero based so we have to decrease it by one
          unixDate = this.$moment(dateArray).unix();
          break;
        default: // hour
          dateArray[1] -= 1; // month must be zero based so we have to decrease it by one
          unixDate = this.$moment(dateArray.slice(0, 3)).hour(dateArray[3]).unix();
          break;
      }
      return unixDate;
    },
    processAverageFirstReplyAnalyticsData() {
      const slots = [
        '0 - 1 hours',
        '1 - 8 hours',
        '8 - 24 hours',
        '> 24 hours',
      ];
      const tableData = {};

      let numbers = Object.keys(this.analyticsData);
      numbers = numbers.filter(number => number !== 'date_points');

      const numberAverage = {};

      numbers.forEach((number) => {
        const numberSecondsKeys = Object.keys(this.analyticsData[number].average_reply_time.seconds);
        numberSecondsKeys.forEach((key) => {
          const keyValues = this.analyticsData[number].average_reply_time.seconds[key];
          if (!numberAverage[key]) {
            numberAverage[key] = {
              seconds: 0,
              count: 0,
            };
          }

          numberAverage[key].seconds = this.analyticsData[number].average_reply_time.seconds[key].seconds;
          numberAverage[key].count = this.analyticsData[number].average_reply_time.seconds[key].count;
        });

        slots.forEach((slot) => {
          // average_reply_time
          this.datePoints.forEach((datePoint) => {
            if (!tableData[datePoint] || !tableData[datePoint][slot]) {
              if (!tableData[datePoint]) {
                tableData[datePoint] = {};
              }
              if (!tableData[datePoint][slot]) {
                tableData[datePoint][slot] = 0;
              }
            }

            if (!tableData[datePoint].Date) {
              if (this.rangeFilter.alias === 'hour') {
                const isAmPmTest = isAmPm(this.$store.getters['user/region']);
                tableData[datePoint].Date = this.$moment(this.convertToUnixDate(datePoint) * 1000).format(isAmPmTest ? 'hh:mma - ' : 'HH:mm - ');
                const dateValue = this.$moment(this.convertToUnixDate(datePoint) * 1000).format(', MMM DD, YYYY');
                const endTime = this.$moment(this.convertToUnixDate(datePoint) * 1000).add(1, 'hour').format(isAmPmTest ? 'hh:mma' : ' kk:mm');
                tableData[datePoint].Date += endTime.trim() === '24:00' ? '23:59' : endTime;
                if (this.datePoints.length > 24) {
                  tableData[datePoint].Date += dateValue;
                }
              } else if (this.rangeFilter.alias === 'week') {
                const startDayFormated = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM DD, YYYY');
                const endDayFormatted = this.$moment(this.convertToUnixDate(datePoint) * 1000).add(6, 'days').format('MMM DD, YYYY');
                tableData[datePoint].Date = `${startDayFormated} - ${endDayFormatted}`;
              } else if (this.rangeFilter.alias === 'month') {
                const startDayFormated = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM, YYYY');
                tableData[datePoint].Date = `${startDayFormated}`; // - ${endDayFormatted}`;
              } else {
                tableData[datePoint].Date = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM DD, YYYY');
              }
            }

            tableData[datePoint][slot] += this.analyticsData[number].average_reply_time.slot[slot] ? this.analyticsData[number].average_reply_time.slot[slot][datePoint] : 0;
          });
        });
      });

      this.datePoints.forEach((datePoint) => {
        const averageSeconds = numberAverage[datePoint] && numberAverage[datePoint].count ? Math.round(numberAverage[datePoint].seconds / numberAverage[datePoint].count) : 0;
        tableData[datePoint]['Average reply time'] = this.durationToString(averageSeconds) || 'No activity';
      });

      this.datePoints.forEach((datePoint) => {
        let datePointTotal = 0;
        slots.forEach((slot) => {
          datePointTotal += tableData[datePoint][slot];
        });

        slots.forEach((slot) => {
          tableData[datePoint][slot] = datePointTotal ? `${Math.round(Number(tableData[datePoint][slot] / datePointTotal * 100))}%` : 'No activity';
        });
      });

      this.tableData = tableData;
    },
    durationToString(seconds) {
      // eslint-disable-next-line no-underscore-dangle
      const durations = duration(seconds, 'seconds')._data;

      const dayDisplay = durations.days > 0 ? `${durations.days}d ` : '';
      const hourDisplay = durations.hours > 0 ? `${durations.hours}h ` : '';
      const minuteDisplay = durations.minutes > 0 ? `${durations.minutes}m` : '';
      const secondsDisplay = durations.seconds > 0 ? `${durations.seconds}s` : '';

      return seconds < 60 ? secondsDisplay : `${dayDisplay}${hourDisplay}${minuteDisplay}`;
    },
    dateRangeChange(range) {
      if (this.rangeFilter.alias === 'week') {
        this.startDate = range[0].startOf('day').unix();
        this.endDate = range[1].endOf('week').endOf('day').unix();
      } else if (this.rangeFilter.alias === 'month') {
        this.startDate = range[0].startOf('day').unix();
        this.endDate = range[1].endOf('month').endOf('day').unix();
      } else {
        this.startDate = range[0].startOf('day').unix();
        this.endDate = range[1].endOf('day').unix();
      }

      const newHideModes = [];
      const differenceInHours = range[1].diff(range[0], 'hours');
      
      // Hide modes if they are too detail for the selected range
      // --------------------------------------------------------------------------------
      // Hide 'hourly' if difference is more than 48 hours
      if (differenceInHours >= 48) {
        newHideModes.push('hour');
      }

      // Hide 'daily' if more than 90 days are selected
      if (differenceInHours >= (24 * 90)) {
        newHideModes.push('day');
      }

      // Hide 'weekly' if more than 50 weeks are selected
      if (differenceInHours >= (24 * 7 * 50)) {
        newHideModes.push('week');
      }

      // Also, hide modes if they are too wide for selected range.
      // E.g. There is no reason 'montly' should be available if user only selected
      //      5 days from the calendar.
      // --------------------------------------------------------------------------------
      // Hide 'montly' if difference is less than 45 days
      if (differenceInHours < (24 * 45)) {
        newHideModes.push('month');
      }

      // Hide 'weekly' if difference is less than 14 days
      if (differenceInHours < (24 * 14)) {
        newHideModes.push('week');
      }

      // Hide 'daily' if difference is less than 24 hours
      if (differenceInHours < 24) {
        newHideModes.push('day');
      }

      if (newHideModes.toString() !== this.hideModes.toString()) {
        this.hideModes = newHideModes;
      }

      this.$nextTick(() => {
        this.getActivityData();
      });
    },
    buildMessagesTableForNumber(numberId) {
      const tableData = {};
      const sectionColumns = this.tableColumns.messages.columns;
      const isAmPmTest = isAmPm(this.$store.getters['user/region']);

      this.datePoints.forEach((datePoint) => {
        tableData[datePoint] = {};
        Object.keys(sectionColumns).forEach((columnKey) => {
          const paths = sectionColumns[columnKey];
          if (tableData[datePoint][columnKey] === undefined) {
            tableData[datePoint][columnKey] = 0;
          }
          paths.forEach((path) => {
            if (path === 'datePoint') {
              if (this.rangeFilter.alias === 'hour') {
                tableData[datePoint][columnKey] = this.$moment(this.convertToUnixDate(datePoint) * 1000).format(isAmPmTest ? 'hh:mma - ' : 'HH:mm - ');
                const dateValue = this.$moment(this.convertToUnixDate(datePoint) * 1000).format(', MMM DD, YYYY');
                const endTime = this.$moment(this.convertToUnixDate(datePoint) * 1000).add(1, 'hour').format(isAmPmTest ? 'hh:mma' : ' kk:mm');
                tableData[datePoint][columnKey] += endTime.trim() === '24:00' ? '23:59' : endTime;
                if (this.datePoints.length > 24) {
                  tableData[datePoint][columnKey] += dateValue;
                }
              } else if (this.rangeFilter.alias === 'week') {
                const startDayFormated = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM DD, YYYY');
                const endDayFormatted = this.$moment(this.convertToUnixDate(datePoint) * 1000).add(6, 'days').format('MMM DD, YYYY');
                tableData[datePoint][columnKey] = `${startDayFormated} - ${endDayFormatted}`;
              } else if (this.rangeFilter.alias === 'month') {
                tableData[datePoint][columnKey] = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM, YYYY');
              } else {
                tableData[datePoint][columnKey] = this.$moment(this.convertToUnixDate(datePoint) * 1000).format('MMM DD, YYYY');
              }
            } else {
              const pathData = this.$lodash.get(this.analyticsData[numberId], path);
              tableData[datePoint][columnKey] += Number(pathData && pathData[datePoint] >= 0 ? pathData[datePoint] : 0);
            }
          });
        });
      });

      return tableData;
    },
    downloadReport() {
      let csvReport = 'data:text/csv;charset=utf-8,';
      const isSingleDay = this.rangeFilter && this.rangeFilter.alias === 'hour';

      if (this.$route.params.section === 'members') {
        this.membersInFocus.forEach((member, index) => {
          const memberFiltered = {
            Member: this.tableData[member].Member,
            'Total conversations': this.tableData[member]['Total conversations'],
            'Outbound messages': this.tableData[member]['Outbound messages'],
            'Average reply time': this.tableData[member]['Average reply time'],
            Date: this.tableData[member].Date,
          };
          if (index === 0) { // Set titles for columns
            csvReport += `"${Object.keys(memberFiltered).join('","')}"\r\n`;
          }
          csvReport += `"${memberFiltered.Member.name}",`;
          csvReport += `"${Object.values(memberFiltered).slice(1).join('","')}"\r\n`;
        });
      } else if (this.$route.params.section === 'average reply time') {
        this.datePoints.forEach((datePoint, index) => {
          const columnOrder = ['Average reply time', '0 - 1 hours', '1 - 8 hours', '8 - 24 hours', '> 24 hours', 'Date'];
          if (index === 0) { // Set titles for columns
            csvReport += `"${columnOrder.join('","')}"\r\n`;
            if (isSingleDay) {
              csvReport = csvReport.replace('"Date"', '"Time"');
            }
          }
          columnOrder.forEach((column) => {
            csvReport += `"${this.tableData[datePoint][column]}",`;
          });
          csvReport = csvReport.replace(/,$/, '');
          csvReport += '\r\n';
        });
      } else {
        const writeTable = (tableData) => {
          this.datePoints.forEach((datePoint, index) => {
            if (index === 0) { // Set titles for columns
              let header = `"${Object.keys(tableData[datePoint]).join('","')}"\r\n`;
              if (isSingleDay) {
                header = header.replace('"Date"', '"Time"');
              }
              csvReport += header;
            }
            csvReport += `"${Object.values(tableData[datePoint]).join('","')}"\r\n`;
          });
        };

        writeTable(this.tableData);

        if (this.$route.params.section === 'messages' && this.analyticsData) {
          const numberIds = Object.keys(this.analyticsData).filter(n => n !== 'date_points');
          if (numberIds.length > 1) {
            numberIds.forEach((numberId) => {
              const business = this.currentOrganisationBusinesses.find(b => b.id === numberId);
              const name = business && business.name ? business.name : numberId;
              const phone = business && business.phone ? ` (${business.phone})` : '';
              const label = `${name}${phone}`.replace(/"/g, '""');
              csvReport += `\r\n"${label}"\r\n`;
              writeTable(this.buildMessagesTableForNumber(numberId));
            });
          }
        }
      }

      const encodedUri = encodeURI(csvReport);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      const section = !this.isIntegration ? this.$route.params.section : this.targetIntegration.name;
      const filename = `${this.$options.filters.firstUppercase(section.replace(/\s/g, '-'))}-report-${this.$moment().format('YYYY-MM-DD-HHmm')}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);

      link.click();
    },
    getActivityData() {
      if (this.preventDataFetch || !this.startDate || !this.endDate || !this.rangeFilter) {
        return;
      }
      const startDate = this.$moment(false, false).unix(this.startDate);
      const endDate = this.$moment(false, false).unix(this.endDate);
      if (this.rangeFilter.alias === 'day' && startDate.date() === endDate.date() && startDate.month() === endDate.month() && startDate.year() === endDate.year()) {
        startDate.startOf('day');
        endDate.endOf('day');
      } else if (this.rangeFilter.alias === 'month' && startDate.month() === endDate.month() && startDate.year() === endDate.year()) {
        startDate.startOf('month');
        endDate.endOf('month');
      }
      this.loadingData = true;

      let userIds;
      if (this.$route.params.section && this.$route.params.section === 'members') {
        userIds = this.membersInFocus.join(',');
        if (!userIds.length) {
          setTimeout(() => {
            this.getActivityData();
          }, 1000);
          return;
        }
      }

      const options = {
        date_point: this.rangeFilter.alias,
        // start_date: this.rangeFilter.alias === 'hour' ? startDate.tz(this.currentOrganisation.timezone).unix() : startDate.tz(this.currentOrganisation.timezone).unix(), // + (this.$moment().utcOffset() * 60),
        // end_date: this.rangeFilter.alias === 'hour' ? endDate.tz(this.currentOrganisation.timezone).endOf('day').unix() : endDate.endOf('day').tz(this.currentOrganisation.timezone).unix(), // + (this.$moment().utcOffset() * 60),
        start_date: startDate.tz(this.currentOrganisation.timezone).unix(), // + (this.$moment().utcOffset() * 60),
        end_date: endDate.tz(this.currentOrganisation.timezone).endOf('day').unix(), // + (this.$moment().utcOffset() * 60),
        category: this.category,
        // user_ids: userIds, // this breaks the API call because of URI too long error and it doesn't seem to be needed
      };
      if (this.numbersFilter.length) {
        options.business_ids = this.numbersFilter.toString();
      }

      this.preventDataFetch = true;
      if (this.$route.params.section === 'contacts') {
        api('GET', `/organisation/${this.currentOrganisation.id}/analytics/contacts`, options, null, 'w')
          .then((response) => {
            if (response.status === 200) {
              this.processChartContactsData(response.data);
            }
            this.loadingData = false;
          })
          .catch((error) => {
            this.error = error.response;
          })
          .finally(() => {
            // this.loadingData = false;
          });
      } else {
        api('GET', `/organisation/${this.currentOrganisation.id}/analytics`, options, null, 'w')
          .then((response) => {
            if (response.status === 200) {
              // response.data['332eba98-3011-4cb8-85d7-bc7e1a3f852d'].integrations = {
              // "Right Move": {
              //     "2022-6-1": {
              //         "count": 242,
              //         "answered_count": 140
              //     },
              //     "2022-6-2": {
              //         "count": 0,
              //         "answered_count": 156
              //     },
              //     "2022-6-3": {
              //         "count": 309,
              //         "answered_count": 0
              //     },
              //     "2022-6-4": {
              //         "count": 0,
              //         "answered_count": 0
              //     },
              //     "2022-6-5": {
              //         "count": 114,
              //         "answered_count": 146
              //     }
              // },
              // "Zoopla": {
              //     "2022-6-1": {
              //         "count": 149,
              //         "answered_count": 89
              //     },
              //     "2022-6-2": {
              //         "count": 158,
              //         "answered_count": 75
              //     },
              //     "2022-6-3": {
              //         "count": 196,
              //         "answered_count": 105
              //     },
              //     "2022-6-4": {
              //         "count": 233,
              //         "answered_count": 130
              //     },
              //     "2022-6-5": {
              //         "count": 93,
              //         "answered_count": 77
              //     }
              // },
              // "Right Move Valuation": {
              //     "2022-6-1": {
              //         "count": 1,
              //         "answered_count": 0
              //     },
              //     "2022-6-2": {
              //         "count": 4,
              //         "answered_count": 1
              //     },
              //     "2022-6-3": 0,
              //     "2022-6-4": {
              //         "count": 2,
              //         "answered_count": 0
              //     },
              //     "2022-6-5": 0
              // }
              // };
              if (this.isIntegration) {
                this.processChartDataIntegration(response.data);
              } else {
                this.processChartData(response.data);
              }
            }
            this.loadingData = false;
          })
          .catch((error) => {
            this.error = error.response;
          })
          .finally(() => {
            // this.loadingData = false;
          });
      }

      setTimeout(() => {
        this.preventDataFetch = false;
      }, 100);
    },
    processChartContactsData(analyticsChartData) {
      this.analyticsData = analyticsChartData;

      const totalContactsPerDatePoint = {};
      const totalContactsTypePerDatePoint = {};

      this.datePoints = [...Object.keys(analyticsChartData)];

      this.datePoints.forEach((datePoint) => {
        if (!totalContactsPerDatePoint[datePoint]) {
          totalContactsPerDatePoint[datePoint] = 0;
          totalContactsTypePerDatePoint[datePoint] = {
            date: this.convertToUnixDate(datePoint),
            new: 0,
          };
        }
        totalContactsPerDatePoint[datePoint] += Number(analyticsChartData[datePoint]);
      });

      this.processContactsDetailsData();

      this.chartsData.messages = {
        ...this.chartsData.messages,
        ...{
          chartSeries: [{
            name: 'data',
            data: Object.values(totalContactsPerDatePoint),
          }],
        },
        ...{
          dataSeries: [{
            name: 'data',
            data: Object.values(totalContactsTypePerDatePoint),
          }],
        },
      };
    },
    processChartData(analyticsData) {
      this.analyticsData = analyticsData;
      this.datePoints = [];
      let numbers = Object.keys(analyticsData);
      numbers = numbers.filter(number => number !== 'date_points');

      // // Find datePoints
      // numbers.forEach((number) => {
      //   this.datePoints =
      //     [
      //       ...this.datePoints,
      //       // if messages
      //       ...Object.keys(analyticsData[number].messages && analyticsData[number].messages.bot ? analyticsData[number].messages.bot : {}),
      //       ...Object.keys(analyticsData[number].messages && analyticsData[number].messages.inbound ? analyticsData[number].messages.inbound : {}),
      //       ...Object.keys(analyticsData[number].messages && analyticsData[number].messages.outbound ? analyticsData[number].messages.outbound : {}),
      //       // if reply time
      //       ...Object.keys(analyticsData[number].average_reply_time && analyticsData[number].average_reply_time.seconds ? analyticsData[number].average_reply_time.seconds : {}),
      //       // if conversations
      //       ...Object.keys(analyticsData[number].conversations && analyticsData[number].conversations.new_conversations ? analyticsData[number].conversations.new_conversations : {}),
      //       // if contacts
      //       ...Object.keys(analyticsData[number].conversations && analyticsData[number].conversations.new_conversations ? analyticsData[number].conversations.new_conversations : {}),
      //     ];
      // });

      // // filter out duplicate datePoints
      // this.datePoints = this.datePoints.filter((v, i, s) => s.indexOf(v) === i);

      this.datePoints = analyticsData.date_points;

      const detailsProcessed = {};
      const totalMessagesPerDatePoint = {};
      const totalMessagesTypePerDatePoint = {};

      const detailsKeys = Object.keys(this.details);

      numbers.forEach((number) => {
        if (this.$route.params.section === 'average reply time') {
          this.$nextTick(() => {
            this.processAverageFirstReplyDetailsData();
          });
        } else {
          detailsKeys.forEach((key) => {
            if (!detailsProcessed[key]) {
              detailsProcessed[key] = 0;
            }
            this.details[key].forEach((detailPath) => {
              try {
                detailsProcessed[key] += Object.values(this.$lodash.get(analyticsData[number], detailPath)).reduce((prev, curr) => prev + curr, 0);
              } catch (error) { }
            });
          });
        }

        if (this.$route.params.section === 'average reply time') {
          const numberData = this.analyticsData[number];
          this.datePoints.forEach((datePoint) => {
            if (!totalMessagesPerDatePoint[datePoint]) {
              totalMessagesPerDatePoint[datePoint] = 0;
              totalMessagesTypePerDatePoint[datePoint] = {
                date: this.convertToUnixDate(datePoint),
                '0 - 1h': 0,
                '1 - 8h': 0,
                '8 - 24h': 0,
                '> 24h': 0,
                'TMP 0 - 1h': {
                  count: 0,
                  seconds: 0,
                },
                'TMP 1 - 8h': {
                  count: 0,
                  seconds: 0,
                },
                'TMP 8 - 24h': {
                  count: 0,
                  seconds: 0,
                },
                'TMP > 24h': {
                  count: 0,
                  seconds: 0,
                },
                totalCount: 0,
                totalSeconds: 0,
              };
            }
            if (numberData.average_reply_time.seconds[datePoint]) {
              totalMessagesPerDatePoint[datePoint] += Number(numberData.average_reply_time.seconds[datePoint].seconds || 0);

              totalMessagesTypePerDatePoint[datePoint].totalCount += Number(numberData.average_reply_time.seconds[datePoint].count || 0);
              totalMessagesTypePerDatePoint[datePoint].totalSeconds += Number(numberData.average_reply_time.seconds[datePoint].seconds || 0);

              if (numberData.average_reply_time.seconds[datePoint].seconds <= 3600) { // less than 1 hour
                totalMessagesTypePerDatePoint[datePoint]['TMP 0 - 1h'].count += Number(numberData.average_reply_time.seconds[datePoint].count || 0);
                totalMessagesTypePerDatePoint[datePoint]['TMP 0 - 1h'].seconds += Number(numberData.average_reply_time.seconds[datePoint].seconds || 0);
                totalMessagesTypePerDatePoint[datePoint]['0 - 1h'] = totalMessagesTypePerDatePoint[datePoint]['TMP 0 - 1h'].count ? totalMessagesTypePerDatePoint[datePoint]['TMP 0 - 1h'].count / totalMessagesTypePerDatePoint[datePoint].totalCount : 0;
              } else if (numberData.average_reply_time.seconds[datePoint].seconds <= 28800) { // less than 8 hours
                totalMessagesTypePerDatePoint[datePoint]['TMP 1 - 8h'].count += Number(numberData.average_reply_time.seconds[datePoint].count || 0);
                totalMessagesTypePerDatePoint[datePoint]['TMP 1 - 8h'].seconds += Number(numberData.average_reply_time.seconds[datePoint].seconds || 0);
                totalMessagesTypePerDatePoint[datePoint]['1 - 8h'] = totalMessagesTypePerDatePoint[datePoint]['TMP 1 - 8h'].count ? totalMessagesTypePerDatePoint[datePoint]['TMP 1 - 8h'].count / totalMessagesTypePerDatePoint[datePoint].totalCount : 0;
              } else if (numberData.average_reply_time.seconds[datePoint].seconds <= 86400) { // less than 24 hours
                totalMessagesTypePerDatePoint[datePoint]['TMP 8 - 24h'].count += Number(numberData.average_reply_time.seconds[datePoint].count || 0);
                totalMessagesTypePerDatePoint[datePoint]['TMP 8 - 24h'].seconds += Number(numberData.average_reply_time.seconds[datePoint].seconds || 0);
                totalMessagesTypePerDatePoint[datePoint]['8 - 24h'] = totalMessagesTypePerDatePoint[datePoint]['TMP 8 - 24h'].count ? totalMessagesTypePerDatePoint[datePoint]['TMP 8 - 24h'].count / totalMessagesTypePerDatePoint[datePoint].totalCount : 0;
              } else { // more than 24 hours
                totalMessagesTypePerDatePoint[datePoint]['TMP > 24h'].count += Number(numberData.average_reply_time.seconds[datePoint].count || 0);
                totalMessagesTypePerDatePoint[datePoint]['TMP > 24h'].seconds += Number(numberData.average_reply_time.seconds[datePoint].seconds || 0);
                totalMessagesTypePerDatePoint[datePoint]['> 24h'] = totalMessagesTypePerDatePoint[datePoint]['TMP > 24h'].count ? totalMessagesTypePerDatePoint[datePoint]['TMP > 24h'].count / totalMessagesTypePerDatePoint[datePoint].totalCount : 0;
              }
              totalMessagesPerDatePoint[datePoint] = totalMessagesTypePerDatePoint[datePoint].totalCount ? Math.round(Number(totalMessagesTypePerDatePoint[datePoint].totalSeconds / totalMessagesTypePerDatePoint[datePoint].totalCount)) : 0;
            }
          });
        } else {
          this.datePoints.forEach((datePoint) => {
            if (!totalMessagesPerDatePoint[datePoint]) {
              totalMessagesPerDatePoint[datePoint] = 0;
              totalMessagesTypePerDatePoint[datePoint] = {
                date: this.convertToUnixDate(datePoint),
                in: 0,
                out: 0,
                bot: 0,
              };
            }

            // total
            this.total.forEach((path) => {
              try {
                totalMessagesPerDatePoint[datePoint] += Number(this.$lodash.get(this.analyticsData[number], path)[datePoint] || 0);
              } catch (error) { }
            });
          });
        }
      });

      this.detailsData = detailsProcessed;

      this.chartsData.messages = {
        ...this.chartsData.messages,
        ...{
          chartSeries: [{
            name: 'data',
            data: Object.values(totalMessagesPerDatePoint),
          }],
        },
        ...{
          dataSeries: [{
            name: 'data',
            data: Object.values(totalMessagesTypePerDatePoint),
          }],
        },
      };
    },
    processChartDataIntegration(analyticsIntegrationData) {
      this.analyticsData = analyticsIntegrationData;
      const integrationsData = {
        datePoints: {},
        datePointDetails: {},
      };

      let numbers = Object.keys(analyticsIntegrationData);
      numbers = numbers.filter(number => number !== 'date_points');

      // // Find datePoints
      // numbers.forEach((number) => {
      //   if (analyticsIntegrationData[number].integrations && analyticsIntegrationData[number].integrations[this.integrationName]) {
      //     const d = Object.keys(analyticsIntegrationData[number].integrations[this.integrationName]);
      //     this.datePoints =
      //       [
      //         ...this.datePoints,
      //         ...d,
      //       ];
      //   }
      // });

      // // filter out duplicate datePoints
      // this.datePoints = this.datePoints.filter((v, i, s) => s.indexOf(v) === i);

      this.datePoints = this.analyticsData.date_points;

      numbers.forEach((number) => {
        this.datePoints.forEach((datePoint) => {
          if (!integrationsData.datePoints[datePoint]) {
            integrationsData.datePoints[datePoint] = Number(0);
            integrationsData.datePointDetails[datePoint] = {
              date: this.convertToUnixDate(datePoint),
              contacted: 0,
              replied: 0,
            };
          }
        });
        if (analyticsIntegrationData[number].integrations && Object.keys(analyticsIntegrationData[number].integrations).length) {
          this.datePoints.forEach((datePoint) => {
            integrationsData.datePoints[datePoint] +=
              (Number(analyticsIntegrationData[number].integrations[this.integrationName][datePoint].count)) > 0
                ? Number(((Number(analyticsIntegrationData[number].integrations[this.integrationName][datePoint].answered_count) / Number(analyticsIntegrationData[number].integrations[this.integrationName][datePoint].count)) * 100).toFixed(2))
                : 0;
            integrationsData.datePointDetails[datePoint].contacted += Number(analyticsIntegrationData[number].integrations[this.integrationName][datePoint].count) ? Number(analyticsIntegrationData[number].integrations[this.integrationName][datePoint].count) : 0;
            integrationsData.datePointDetails[datePoint].replied += Number(analyticsIntegrationData[number].integrations[this.integrationName][datePoint].answered_count) ? Number(analyticsIntegrationData[number].integrations[this.integrationName][datePoint].answered_count) : 0;
          });
        }
      });

      this.processIntegrationsDetailsData();

      this.chartsData.messages = {
        ...this.chartsData.messages,
        chartDetails: [
          { name: 'contacted', text: 'contacted' },
          { name: 'replied', text: 'replied' },
        ],
        chartSeries: [{
          name: 'data',
          data: Object.values(integrationsData.datePoints),
        }],
        dataSeries: [{
          name: 'data',
          data: Object.values(integrationsData.datePointDetails),
        }],
      };
    },
    processAverageFirstReplyDetailsData() {
      const detailsProcessed = {
        'AVERAGE REPLY TIME': 0,
        '0 - 1 HOURS': 0,
        '1 - 8 HOURS': 0,
        '8 - 24 HOURS': 0,
        '> 24 HOURS': 0,
      };

      if (this.hoverIndex !== null) {
        const targetDatePoint = this.datePoints[this.hoverIndex];
        detailsProcessed['AVERAGE REPLY TIME'] = this.tableData[targetDatePoint]['Average reply time'];
        detailsProcessed['0 - 1 HOURS'] = this.tableData[targetDatePoint]['0 - 1 hours'];
        detailsProcessed['1 - 8 HOURS'] = this.tableData[targetDatePoint]['1 - 8 hours'];
        detailsProcessed['8 - 24 HOURS'] = this.tableData[targetDatePoint]['8 - 24 hours'];
        detailsProcessed['> 24 HOURS'] = this.tableData[targetDatePoint]['> 24 hours'];
      } else if (this.chartsData.messages.dataSeries && this.chartsData.messages.dataSeries[0] && this.chartsData.messages.dataSeries[0].data) {
        let totalSeconds = 0;
        let totalMessages = 0;
        const totalSlots = {
          '0 - 1': 0,
          '1 - 8': 0,
          '8 - 24': 0,
          '> 24': 0,
        };
        this.chartsData.messages.dataSeries[0].data.forEach((data, index) => {
          totalMessages += data.totalCount;
          totalSeconds += data.totalSeconds;
        });

        detailsProcessed['AVERAGE REPLY TIME'] = totalMessages ? this.durationToString(Math.round(Number(totalSeconds / totalMessages || 0))) : 'No activity';

        if (!totalMessages) {
          detailsProcessed['0 - 1 HOURS'] = 'No activity';
          detailsProcessed['1 - 8 HOURS'] = 'No activity';
          detailsProcessed['8 - 24 HOURS'] = 'No activity';
          detailsProcessed['> 24 HOURS'] = 'No activity';
        } else {
          let numbers = Object.keys(this.analyticsData);
          numbers = numbers.filter(number => number !== 'date_points');

          numbers.forEach((number) => {
            totalSlots['0 - 1'] += this.analyticsData[number].average_reply_time.slot['0 - 1 hours'] ? Object.values(this.analyticsData[number].average_reply_time.slot['0 - 1 hours'] ? this.analyticsData[number].average_reply_time.slot['0 - 1 hours'] : {}).reduce((prev, curr) => prev + curr, 0) : 0;
            totalSlots['1 - 8'] += this.analyticsData[number].average_reply_time.slot['1 - 8 hours'] ? Object.values(this.analyticsData[number].average_reply_time.slot['1 - 8 hours'] ? this.analyticsData[number].average_reply_time.slot['1 - 8 hours'] : {}).reduce((prev, curr) => prev + curr, 0) : 0;
            totalSlots['8 - 24'] += this.analyticsData[number].average_reply_time.slot['8 - 24 hours'] ? Object.values(this.analyticsData[number].average_reply_time.slot['8 - 24 hours'] ? this.analyticsData[number].average_reply_time.slot['8 - 24 hours'] : {}).reduce((prev, curr) => prev + curr, 0) : 0;
            totalSlots['> 24'] += this.analyticsData[number].average_reply_time.slot['> 24 hours'] ? Object.values(this.analyticsData[number].average_reply_time.slot['> 24 hours'] ? this.analyticsData[number].average_reply_time.slot['> 24 hours'] : {}).reduce((prev, curr) => prev + curr, 0) : 0;
          });

          const totalConversations = totalSlots['0 - 1'] + totalSlots['1 - 8'] + totalSlots['8 - 24'] + totalSlots['> 24'];

          detailsProcessed['0 - 1 HOURS'] = `${totalConversations ? Math.round(Number(totalSlots['0 - 1'] / totalConversations * 100)) : 0}%`;
          detailsProcessed['1 - 8 HOURS'] = `${totalConversations ? Math.round(Number(totalSlots['1 - 8'] / totalConversations * 100)) : 0}%`;
          detailsProcessed['8 - 24 HOURS'] = `${totalConversations ? Math.round(Number(totalSlots['8 - 24'] / totalConversations * 100)) : 0}%`;
          detailsProcessed['> 24 HOURS'] = `${totalConversations ? Math.round(Number(totalSlots['> 24'] / totalConversations * 100)) : 0}%`;
        }
      }

      this.$nextTick(() => {
        this.detailsData = { ...detailsProcessed };
      });
    },
    processDetailsData() {
      let numbers = Object.keys(this.analyticsData);
      numbers = numbers.filter(number => number !== 'date_points');
      const detailsProcessed = {};

      const detailsKeys = Object.keys(this.details);

      numbers.forEach((number) => {
        detailsKeys.forEach((key) => {
          if (!detailsProcessed[key]) {
            detailsProcessed[key] = 0;
          }
          this.details[key].forEach((detailPath) => {
            if (this.hoverIndex !== null) {
              try {
                detailsProcessed[key] += this.$lodash.get(this.analyticsData[number], detailPath)[this.datePoints[this.hoverIndex]] || 0;
              } catch (error) { }
            } else {
              try {
                detailsProcessed[key] += Object.values(this.$lodash.get(this.analyticsData[number], detailPath)).reduce((prev, curr) => prev + curr, 0);
              } catch (error) { }
            }
          });
        });
      });
      this.detailsData = detailsProcessed;
    },
    processIntegrationsDetailsData() {
      let numbers = Object.keys(this.analyticsData);
      numbers = numbers.filter(number => number !== 'date_points');
      const detailsProcessed = {};
      const detailsKeys = Object.keys(this.details);

      numbers.forEach((number) => {
        detailsKeys.forEach((key) => {
          if (!detailsProcessed[key]) {
            detailsProcessed[key] = 0;
          }
        });
        if (this.analyticsData[number].integrations && Object.keys(this.analyticsData[number].integrations).length) {
          detailsKeys.forEach((key) => {
            this.details[key].forEach((detailItem) => {
              if (this.hoverIndex !== null && detailItem !== '') {
                detailsProcessed[key] += this.analyticsData[number].integrations[this.integrationName][this.datePoints[this.hoverIndex]][detailItem] || 0;
              } else if (this.hoverIndex !== null && detailItem === '') {
                const hoverIndexMessages = this.analyticsData[number].integrations[this.integrationName][this.datePoints[this.hoverIndex]].count;
                const rate = `${hoverIndexMessages ? Math.round(this.analyticsData[number].integrations[this.integrationName][this.datePoints[this.hoverIndex]].answered_count / hoverIndexMessages * 100) : 0}%`;
                detailsProcessed[key] = rate || 0;
              } else if (!this.hoverIndex && detailItem !== '') {
                detailsProcessed[key] += Object.values(this.analyticsData[number].integrations[this.integrationName]).reduce((prev, curr) => prev + Number(curr[detailItem] || 0), 0);
              }
              if (!this.hoverIndex) {
                const totalMessages = detailsProcessed.CONTACTED;
                const rate = `${totalMessages ? Math.round(detailsProcessed.REPLIED / totalMessages * 100) : 0}%`;
                detailsProcessed['CONVERSION RATE'] = rate || 0;
              }
            });
          });
        }
      });
      this.detailsData = detailsProcessed;
    },
    processContactsDetailsData() {
      const detailsProcessed = {};
      const detailsKeys = Object.keys(this.details);

      detailsKeys.forEach((key) => {
        if (!detailsProcessed[key]) {
          detailsProcessed[key] = 0;
        }
        this.details[key].forEach((detailPath) => {
          if (this.hoverIndex !== null && detailPath !== '') {
            detailsProcessed[key] += this.$lodash.get(this.analyticsData, detailPath)[this.datePoints[this.hoverIndex]] || 0;
          } else if (this.hoverIndex !== null && detailPath === '') {
            detailsProcessed[key] += this.analyticsData[this.datePoints[this.hoverIndex]] || 0;
          } else if (!this.hoverIndex && detailPath !== '') {
            detailsProcessed[key] += Object.values(this.$lodash.get(this.analyticsData, detailPath)).reduce((prev, curr) => prev + curr, 0);
          } else {
            detailsProcessed[key] += Object.values(this.analyticsData).reduce((prev, curr) => prev + curr, 0);
          }
        });
      });

      this.detailsData = detailsProcessed;
    },
    hoverIndexChange(hoverIndex) {
      this.hoverIndex = hoverIndex >= 0 ? hoverIndex : null;
    },
  },
};
</script>

<style lang="scss" scoped>
.dashboard {
  background-color: $white;
  padding: 0 0 35px;
  overflow-x: hidden;
  overflow-y: auto;

  .sb2_reports_title {
    font-size: 22px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 0.87;
    letter-spacing: normal;
    text-align: left;
    color: #141c20;

    margin-top: 35px;
    margin-bottom: 0;

    ::v-deep .back-btn {
      font-weight: bold;

      &:hover {
        cursor: pointer;

        .sb-icon .icon.mask {
            background-color: $blackPrimary !important;
        }
      }
    }
  }

  .title {
    margin-bottom: 12px !important;
  }

  .row {
    max-width: 1521px;
    margin-left: 40px;
    margin-right: 40px;

    &.sticky-top {
      background-color: $white;
      z-index: 2000;
      &.second-sticky {
            z-index: 1900;
      }
    }

    .left-40 {
      padding-left: 40px;
    }

    .right-40 {
      padding-right: 40px;
    }

    .action-bar {
      margin-top: 21px;

      .settings--box {
        padding-bottom: 15px;

        &:not(.no-border) {
          border-bottom: solid 1px #e6ebed;
        }

        .download {
          max-height: 30px;
        }
      }

      .range-filter {
        width: fit-content;
        &.multiselect {
          min-height: 30px !important;
        }
        ::v-deep {

          .multiselect__tags {
            padding: 0 35px 0 15px !important;
            min-height: 30px;
          }

          .multiselect__select {
            margin-right: 15px;
            height: 30px;
          }
        }
      }

      .min-130 {
        min-width: 130px;
      }
    }
  }

  ::v-deep .multiselect {
    .multiselect__single {
      font-weight: 500;
    }

    .multiselect__content {
      padding: 8px 0 !important;
    }

    &.flex-options .multiselect__option {
      padding: 10px 10px !important;

      span {
        color: $blackPrimary;
      }
    }
  }

  .chart {
    // overflow: hidden;
  }
}

.table__grid {
  width: 100%;
  grid-template-columns: var(--grid-template-columns);

  .full-width {
    grid-column: 1 / span 4 !important;
  }

  .column-name.sticky-top {
    top: 66px;
    background: $white;
  }
}

.no-border {
  border-top: none !important;
}

::v-deep .multiselect {
  div.multiselect__content-wrapper {
    width: fit-content;
    min-width: 158px;
    margin-top: 9px;
  }
}

.member-loader {
  min-height: 279px;

  .member-loader-wrapper {
    width: 58px;
    height: 62px;
    border-radius: 15px;
    background-color: #173e51;
    margin-bottom: 26px;
  }

  p {
    font-size: 14px;
    font-weight: 400;
    color: #8195a0;
  }
}

.report-header {
  height: 343px;
  margin: 29px 40px 35px 40px;
  border-radius: 5px;
  background-color: #0b2a3e;
  color: $white;

  .col {
    padding: 40px !important;
  }

  .date-range {
    text-transform: uppercase;
    opacity: 0.5;
    font-family: HelveticaNeue !important;
    font-size: 14px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    color: $white;
  }

  .dashed-right {
    border-right: dashed 1px rgba($white, 0.2);
  }

  .details-container {
    margin-top: 18px;
    height: calc(100% - 35px);
    &.v-scroll-visible {
      padding-right: 10px;
    }

    .details {
      display: flex;
      margin: 0 0 19px 0;
      
      span {
        font-size: 14px !important;
        font-weight: 500 !important;
        color: $white !important;
      }

      span:first-child {
        flex-grow: 2;
      }

      span:last-child {
        opacity: 0.8;
      }

      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }
}
</style>
